'use client';

import { useCombobox } from 'downshift';
import { resolveHref } from '@/utils/pathName';
import { useState, useRef, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import { createPortal } from 'react-dom';
import {
  searchOptions,
  SearchOption,
  itemTypeI18n,
  itemTypeEmoji,
} from '@/components/data/searchOptions';
import { usePathname, useRouter } from 'next/navigation';

export default function SearchBar() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const handleKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === '/' || (event.key === 'k' && event.metaKey)) {
      inputRef.current?.focus();
      event.preventDefault();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  const [searchResults, setSearchResults] = useState<SearchOption[]>([]);
  const pathName = usePathname() ?? '';
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    reset,
    inputValue,
    closeMenu,
  } = useCombobox({
    onInputValueChange({ inputValue }) {
      if (!inputValue) {
        setSearchResults([]);
        return;
      }

      setSearchResults(
        searchOptions.filter((option: SearchOption) =>
          option.name.toLowerCase().includes(inputValue.toLowerCase())
        )
      );
    },
    onSelectedItemChange({ selectedItem }) {
      if (!selectedItem) {
        return;
      }

      reset();
      closeMenu();
      inputRef.current?.blur();

      const href = `/${selectedItem.type}/${selectedItem.id}`;
      const resolvedHref = resolveHref(pathName, href);
      router.push(resolvedHref);
    },
    items: searchResults,
    itemToString(item) {
      return item ? item.name : '';
    },
  });

  return (
    <>
      <input
        placeholder="Search"
        className="w-full max-w-xs rounded-3xl bg-gray-100 px-3 py-1 dark:bg-gray-800"
        {...getInputProps({ ref: inputRef })}
      />

      {mounted &&
        createPortal(
          <div
            className={clsx(
              'fixed left-0 top-14 z-20 flex h-full w-full bg-white dark:bg-gray-900/90',
              !inputValue && 'hidden'
            )}
          >
            {!searchResults.length && inputValue && (
              <div className="mx-auto mt-8">
                <p className="text-xl font-semibold dark:text-gray-300">
                  {!inputValue
                    ? 'Search for foods, groups and nutrients.'
                    : 'No results.'}
                </p>
              </div>
            )}

            <ul
              className={`mx-auto w-full max-w-xl shadow-md dark:bg-gray-900 ${
                !searchResults.length && 'hidden'
              }`}
              {...getMenuProps({}, { suppressRefError: false })}
            >
              {searchResults.map((item, index) => (
                <li
                  className={clsx(
                    'flex justify-between px-3 py-2 font-semibold shadow-sm',
                    highlightedIndex === index && 'bg-gray-100 dark:bg-gray-800'
                  )}
                  key={`${item.type}-${item.id}`}
                  {...getItemProps({ item, index })}
                >
                  <span className="text-xl capitalize">{item.name}</span>

                  <span className="text-md text-gray-600 dark:text-gray-100">
                    {`${itemTypeEmoji[item.type]} ${
                      itemTypeI18n[item.type].en
                    }`}
                  </span>
                </li>
              ))}
            </ul>
          </div>,
          document?.body ?? ''
        )}
    </>
  );
}
